import { updateQueryParam } from '@repo/shared/shared';
import { useSearchParams } from 'next/navigation';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { useMemo } from 'react';
import type { QuantityDiscount } from '@/entities/product';
import { OptionSelector } from '@/shared/ui/OptionSelector';
import {
  createQuantityOptions,
  getDiscountDescription,
  prepareQuantity,
  QuantityOption,
} from '../../lib/quantityUtils';
import QuantityOptionCard from './QuantityOptionCard';

interface QuantitySelectorProps {
  slug: string;
  price: number;
  quantityDiscounts?: QuantityDiscount[];
}

const QuantitySelector = ({ slug, price, quantityDiscounts }: QuantitySelectorProps) => {
  const { t } = useTranslation();
  const router = useRouter();
  const searchParams = useSearchParams();

  const quantity = prepareQuantity(searchParams?.get('quantity'));

  const discountDescription = useMemo(() => {
    return getDiscountDescription(quantityDiscounts);
  }, [quantityDiscounts]);

  const quantityOptions = useMemo(() => {
    return createQuantityOptions({ slug, t, price, quantityDiscounts });
  }, [slug, price, quantityDiscounts, t]);

  const onChange = (newQuantity: string) => {
    updateQueryParam(router, 'quantity', newQuantity);
  };

  const renderQuantityOption = (option: QuantityOption) => {
    return (
      <QuantityOptionCard
        option={option}
        price={price}
      />
    );
  };

  return (
    <OptionSelector
      options={quantityOptions}
      renderOption={renderQuantityOption}
      title={discountDescription}
      value={String(quantity)}
      onChange={onChange}
    />
  );
};

export default QuantitySelector;
